import {
  type FC,
  type PropsWithChildren,
  type RefObject,
  useContext,
  useEffect,
  useRef,
} from 'react';
import { toShortNumFormat } from '@common/lib/toShortNumFormat';
import { IntroContext } from '@components/Intro/context.ts';
import { Container } from '@components/ui/Container/container';
import { ValueTag } from '@components/ui/ValueTag/ValueTag';
import { useAppSelector } from '@redux/store';
import cn from 'classnames';
import { AnimatePresence } from 'framer-motion';

import { HeaderAnimationContext } from '@/context/HeaderAnimationContext';

import { CompleteContractAnimationContext } from '../Animations/CompleteContractAnimation/context';
import { MotionPicture } from '../ui/Picture/Picture';

import cashPng from '/img/icons/cash.png';
import cashWebp from '/img/icons/cash.webp';

interface BalanceProps extends PropsWithChildren {
  kind: 'cash' | 'tgem' | 'usdt';
  onClick?: () => void;
  className?: string;
}

type RefWithGetImage = HTMLDivElement & {
  getImageElement: () => RefObject<HTMLDivElement>;
};

const Balance: FC<BalanceProps> = ({ kind, children, onClick, className }) => {
  const { animationStep, setAnimationStep } = useContext(
    CompleteContractAnimationContext,
  );
  const { tgemIconRef } = useContext(IntroContext);
  const cashBalance = useAppSelector((state) => state.user.user?.balance.cash);
  const tgemBalance = useAppSelector((state) => state.user.user?.balance.tgem);
  const balanceRef = useRef<RefWithGetImage | null>(null);
  const {
    setCashBalanceRef,
    cashBalanceScale,
    setTgemBalanceRef,
    tgemBalanceScale,
  } = useContext(HeaderAnimationContext);
  const usdtBalance = useAppSelector((state) => state.user.user?.balance?.usdt);

  let value;

  switch (kind) {
    case 'usdt':
      value = Number((usdtBalance || 0).toFixed(2));
      break;
    case 'cash':
      value = cashBalance;
      break;
    case 'tgem':
      value = tgemBalance;
      break;
    default:
      value = 0;
  }

  useEffect(() => {
    if (kind === 'cash') {
      setCashBalanceRef(balanceRef.current?.getImageElement?.() ?? null);
    } else if (kind === 'tgem') {
      setTgemBalanceRef(balanceRef.current?.getImageElement?.() ?? null);
    }
  }, [kind]);

  return (
    <Container
      variant="blur-20"
      className={cn(
        'flex items-center h-[32px] pl-[3.2px] pr-[8px]',
        className,
      )}
      onClick={onClick}
    >
      {kind === 'tgem' ? (
        <ValueTag
          ref={(ref) => {
            tgemIconRef(ref);
            balanceRef.current = ref as RefWithGetImage;
          }}
          kind={kind}
          value={toShortNumFormat(value || 0)}
          variant="balance"
          fontWeight="bold"
          scale={tgemBalanceScale}
        />
      ) : (
        <div className="relative">
          <ValueTag
            kind={kind}
            value={toShortNumFormat(value || 0)}
            variant="balance"
            fontWeight="bold"
            ref={balanceRef}
            scale={cashBalanceScale && kind === 'cash'}
          />
          <AnimatePresence>
            {animationStep === 2 && (
              <MotionPicture
                layoutId="contract-complete-cash"
                transition={{ layout: { duration: 0.6, ease: 'easeOut' } }}
                exit={{
                  opacity: 0,
                  transition: { duration: 0.5, ease: 'easeOut' },
                }}
                onLayoutAnimationComplete={() => {
                  setAnimationStep(3);
                }}
                img={cashPng}
                webp={cashWebp}
                className="w-[25.6px] h-[25.6px] shink-0 absolute left-0 top-0"
              />
            )}
          </AnimatePresence>
        </div>
      )}
      {children}
    </Container>
  );
};

export { Balance };
